import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Grid,
  MenuItem,
  Card,
  CardContent,
  IconButton,
  Chip,
} from "@mui/material";
import CustomAppBar from "components/CustomHeader";
import Footer from "components/Footer";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    useCase: "",
    email: "",
    message: "",
  });

  const useCases = [
    "Team Engagement",
    "Corporate Event",
    "Employee Onboarding",
    "Training & Development",
    "Community Building",
    "Product Launch",
    "Conference/Webinar",
    "Other",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    // Handle form submission here
  };

  return (
    <Box>
      <CustomAppBar />

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        {/* Hero Contact Section */}
        <Box
          sx={{
            minHeight: "90vh",
            display: "flex",
            alignItems: "center",
            padding: "80px 0 60px 0", // Added top padding for spacing from AppBar
            color: "white",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                repeating-linear-gradient(
                  90deg,
                  transparent,
                  transparent 98px,
                  rgba(255, 255, 255, 0.03) 100px
                ),
                repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 98px,
                  rgba(255, 255, 255, 0.03) 100px
                )
              `,
              pointerEvents: "none",
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            {/* Center Aligned Header */}
            <Box sx={{ textAlign: "center", marginBottom: "60px" }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "8px",
                  pt: 10,
                  color: "white",
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  background: "linear-gradient(45deg, #fff, #f0f8ff)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                }}
              >
                Let's Bring Bingo to Your Team or Event
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  marginBottom: "40px",
                  opacity: 0.9,
                  fontSize: "18px",
                  lineHeight: "1.6",
                  fontWeight: "300",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Ready to level up your team engagement? Let's create an epic
                bingo experience that brings your team together!
              </Typography>
            </Box>

            <Grid container spacing={6} alignItems="center">
              {/* Left Side - Contact Form */}
              <Grid item xs={12} md={6}>
                <Box sx={{ maxWidth: "500px", margin: "0 auto" }}>
                  <Card
                    sx={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "20px",
                      padding: "30px",
                      boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                    }}
                  >
                    <CardContent sx={{ padding: "0 !important" }}>
                      <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "20px",
                        }}
                      >
                        <TextField
                          name="name"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={handleInputChange}
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "12px",
                              backdropFilter: "blur(10px)",
                              transition: "all 0.3s ease",
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.15)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.5)",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4facfe",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "white",
                              "&::placeholder": {
                                color: "rgba(255, 255, 255, 0.7)",
                                opacity: 1,
                              },
                            },
                          }}
                        />

                        <TextField
                          name="organization"
                          placeholder="Organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "12px",
                              backdropFilter: "blur(10px)",
                              transition: "all 0.3s ease",
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.15)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.5)",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4facfe",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "white",
                              "&::placeholder": {
                                color: "rgba(255, 255, 255, 0.7)",
                                opacity: 1,
                              },
                            },
                          }}
                        />

                        <TextField
                          name="useCase"
                          select
                          placeholder="Select your use case"
                          value={formData.useCase}
                          onChange={handleInputChange}
                          variant="outlined"
                          label="Use Cases"
                          fullWidth
                          displayEmpty
                          InputLabelProps={{
                            style: { color: "white" }, // label text color
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "12px",
                              backdropFilter: "blur(10px)",
                              transition: "all 0.3s ease",
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.15)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.5)",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4facfe",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "white",
                              "&::placeholder": {
                                color: "rgba(255, 255, 255, 0.7)",
                                opacity: 1,
                              },
                            },
                            "& .MuiSelect-select": {
                              color: "white",
                              padding: "16px 14px",
                              "&.MuiSelect-select[aria-expanded='false']": {
                                color: formData.useCase
                                  ? "white"
                                  : "rgba(255, 255, 255, 0.7)",
                              },
                            },
                            "& .MuiSelect-icon": {
                              color: "rgba(255, 255, 255, 0.7)",
                              transition: "all 0.3s ease",
                              fontSize: "1.5rem",
                            },
                            "&:hover .MuiSelect-icon": {
                              color: "rgba(255, 255, 255, 0.9)",
                              transform: "rotate(180deg)",
                            },
                            "& .Mui-focused .MuiSelect-icon": {
                              color: "#4facfe",
                              transform: "rotate(180deg)",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: "transparent",
                                backdropFilter: "blur(25px)",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                                borderRadius: "16px",
                                boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
                                marginTop: "8px",
                                maxHeight: "300px",
                                overflow: "auto",
                                "&::-webkit-scrollbar": {
                                  width: "8px",
                                },
                                "&::-webkit-scrollbar-track": {
                                  background: "rgba(255, 255, 255, 0.1)",
                                  borderRadius: "10px",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                  background: "rgba(255, 255, 255, 0.3)",
                                  borderRadius: "10px",
                                  "&:hover": {
                                    background: "rgba(255, 255, 255, 0.5)",
                                  },
                                },
                                "& .MuiList-root": {
                                  padding: "8px",
                                },
                                "& .MuiMenuItem-root": {
                                  color: "white",
                                  transition: "all 0.3s ease",
                                  padding: "12px 16px",
                                  borderRadius: "8px",
                                  margin: "2px 0",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  position: "relative",
                                  overflow: "hidden",
                                  "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: "-100%",
                                    width: "100%",
                                    height: "100%",
                                    background:
                                      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
                                    transition: "left 0.5s ease",
                                  },
                                  "&:hover": {
                                    backgroundColor:
                                      "rgba(255, 255, 255, 0.15)",
                                    transform: "translateX(8px)",
                                    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                                    "&::before": {
                                      left: "100%",
                                    },
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "rgba(79, 172, 254, 0.3)",
                                    color: "white",
                                    fontWeight: "600",
                                    "&::after": {
                                      content: '"✓"',
                                      position: "absolute",
                                      right: "12px",
                                      color: "#4facfe",
                                      fontWeight: "bold",
                                    },
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(79, 172, 254, 0.4)",
                                    },
                                  },
                                  "&.Mui-focusVisible": {
                                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                                    outline:
                                      "2px solid rgba(79, 172, 254, 0.5)",
                                    outlineOffset: "2px",
                                  },
                                },
                              },
                            },
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                          }}
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span
                                  style={{
                                    color: "rgba(255, 255, 255, 0.7)",
                                    fontWeight: "400",
                                    fontSize: "16px",
                                  }}
                                >
                                  Select your use case
                                </span>
                              );
                            }
                            return (
                              <span
                                style={{
                                  color: "white",
                                  fontWeight: "500",
                                  fontSize: "16px",
                                }}
                              >
                                {selected}
                              </span>
                            );
                          }}
                        >
                          {useCases.map((useCase) => (
                            <MenuItem key={useCase} value={useCase}>
                              {useCase}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          name="email"
                          placeholder="Email Address"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "12px",
                              backdropFilter: "blur(10px)",
                              transition: "all 0.3s ease",
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.15)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.5)",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4facfe",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "white",
                              "&::placeholder": {
                                color: "rgba(255, 255, 255, 0.7)",
                                opacity: 1,
                              },
                            },
                          }}
                        />

                        <TextField
                          name="message"
                          placeholder="Tell us about your event or team needs..."
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleInputChange}
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "12px",
                              backdropFilter: "blur(10px)",
                              transition: "all 0.3s ease",
                              "& fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.3)",
                              },
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.15)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(255, 255, 255, 0.5)",
                              },
                              "&.Mui-focused": {
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#4facfe",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputBase-input": {
                              color: "white",
                              "&::placeholder": {
                                color: "rgba(255, 255, 255, 0.7)",
                                opacity: 1,
                              },
                            },
                          }}
                        />

                        <Button
                          type="submit"
                          variant="contained"
                          sx={{
                            background: "white",
                            color: "black",
                            padding: "15px 40px",
                            borderRadius: "30px",
                            fontSize: "18px",
                            fontWeight: "bold",
                            textTransform: "none",
                            alignSelf: "center",
                            // boxShadow: "0 10px 30px rgba(247, 247, 247, 0.4)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: "transparent",
                              transform: "translateY(-3px)",
                              color: "white",
                              // boxShadow: "0 15px 40px rgba(252, 241, 238, 0.6)",
                            },
                          }}
                        >
                          🚀 Let's Start the Game!
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>

              {/* Right Side - Gaming Character */}
              {/* Right Side - Gaming Character */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    textAlign: "center",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    minHeight: "400px", // Add minimum height to ensure proper centering
                  }}
                >
                  <img
                    src="https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExZHVvcGFmaHl5YjJlcnZzOHpvY2o2Ynl0cXc4MXNlMzJyNjZ6eTIwNyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/JrXpEIKc4L7HFbbGyk/giphy.gif"
                    alt="Gaming Character"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "20px",
                      boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
                      border: "3px solid rgba(255, 255, 255, 0.3)",
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Modern Connect Section */}
        <Box
          sx={{
            // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            padding: "80px 0",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <Box sx={{ textAlign: "center", marginBottom: "60px" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "10px",
                  fontSize: { xs: "2rem", md: "3rem" },
                  background: "white",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                🎯 Multiple Ways to Connect
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.8,
                  fontSize: "18px",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Choose your preferred way to reach out and let's create
                something amazing together!
              </Typography>
            </Box>

            <Grid container spacing={4} justifyContent="center">
              {/* Email Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "white",
                    backdropFilter: "blur(20px)",
                    // border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "20px",
                    padding: "30px 20px",
                    textAlign: "center",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      color: "white",
                      // boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                      background: "transparent",
                      // border: "1px solid rgba(79, 172, 254, 0.5)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "linear-gradient(45deg, #4facfe, #00f2fe)",
                        margin: "0 auto 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "32px",
                        boxShadow: "0 10px 30px rgba(79, 172, 254, 0.3)",
                      }}
                    >
                      📧
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        marginBottom: "10px",
                        "&:hover": {
                          color: "white",
                        },
                      }}
                    >
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8 }}>
                      ricky@mybingo.social
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* WhatsApp Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "white",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "20px",
                    padding: "30px 20px",
                    textAlign: "center",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      color: "white",
                      // boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                      background: "transparent",
                      // border: "1px solid rgba(79, 172, 254, 0.5)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "linear-gradient(45deg, #25D366, #128C7E)",
                        margin: "0 auto 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "32px",
                        boxShadow: "0 10px 30px rgba(37, 211, 102, 0.3)",
                      }}
                    >
                      📱
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", marginBottom: "10px" }}
                    >
                      WhatsApp / Phone
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8 }}>
                      +91 9497672721
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Calendar Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "white",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "20px",
                    padding: "30px 20px",
                    textAlign: "center",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      color: "white",
                      // boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                      background: "transparent",
                      // border: "1px solid rgba(79, 172, 254, 0.5)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "linear-gradient(45deg, #ff6b35, #f7931e)",
                        margin: "0 auto 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "32px",
                        boxShadow: "0 10px 30px rgba(255, 107, 53, 0.3)",
                      }}
                    >
                      🗓️
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", marginBottom: "10px" }}
                    >
                      Book a Demo
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8 }}>
                      Schedule a live demo session
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Social Media Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    background: "white",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "20px",
                    padding: "30px 20px",
                    textAlign: "center",
                    height: "100%",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      color: "white",
                      // boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                      background: "transparent",
                      // border: "1px solid rgba(79, 172, 254, 0.5)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "linear-gradient(45deg, #8B4513, #A0522D)",
                        margin: "0 auto 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "32px",
                        boxShadow: "0 10px 30px rgba(139, 69, 19, 0.3)",
                      }}
                    >
                      🌐
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", marginBottom: "10px" }}
                    >
                      Social Media
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8 }}>
                      @MyBingoSocial
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Call to Action */}
            <Box sx={{ textAlign: "center", marginTop: "60px" }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "20px",
                  background: "linear-gradient(45deg, #fff, #f0f8ff)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Ready to create unforgettable team experiences? 🎮
              </Typography>
              <Button
                variant="contained"
                sx={{
                  background: "white",
                  color: "black",
                  padding: "15px 40px",
                  borderRadius: "30px",
                  fontSize: "18px",
                  fontWeight: "bold",
                  textTransform: "none",
                  // boxShadow: "0 10px 30px rgba(79, 172, 254, 0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "transparent",
                    transform: "translateY(-3px)",
                    color: "white",
                    // boxShadow: "0 15px 40px rgba(79, 172, 254, 0.6)",
                  },
                }}
              >
                🚀 Let's Make It Happen!
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default ContactSection;
